/**
 * A dónde lleva la fase actual del ciclo (NAV-02).
 *
 * El chip de fase del encabezado y la línea de tiempo del Inicio enlazan a la
 * pantalla donde se hace el trabajo de esa fase. Antes había un catálogo de
 * atajos por fase que ninguna pantalla usaba (y mandaba a "Tomar lecturas",
 * que en el admin no existe: las lecturas se toman en la app de terreno y acá
 * se revisan).
 */

import type { CicloFase } from "./fases";

export interface DestinoFase {
  to: string;
  /** Texto del enlace, con verbo: dice qué se va a hacer allá. */
  etiqueta: string;
  /** Permiso que exige la pantalla destino (el mismo del menú lateral). */
  permiso: string;
}

export interface ContextoDestinoFase {
  /** Hay un cierre validado esperando facturar (ítem `cierre_facturar`). */
  hayCierrePorFacturar: boolean;
  tienePermiso: (permiso: string) => boolean;
}

const CIERRES: DestinoFase = {
  to: "/cierres",
  etiqueta: "Ir a cierres",
  permiso: "cierres.read",
};

const ENTREGAS: DestinoFase = {
  to: "/entregas",
  etiqueta: "Ver entrega de boletas",
  permiso: "entregas.read",
};

function candidatos(
  fase: CicloFase,
  ctx: ContextoDestinoFase,
): DestinoFase[] {
  switch (fase) {
    case "lecturas":
      return [
        { to: "/lecturas", etiqueta: "Revisar lecturas", permiso: "lecturas.read" },
      ];
    case "cierre":
      return [CIERRES];
    case "emision":
      // Emitir = facturar el cierre y después repartir. Mientras el cierre
      // siga sin facturar, el trabajo está en Cierres; facturado, en la ronda.
      return ctx.hayCierrePorFacturar
        ? [{ ...CIERRES, etiqueta: "Facturar el cierre" }, ENTREGAS]
        : [ENTREGAS, CIERRES];
    case "cobranza":
      return [{ to: "/caja", etiqueta: "Abrir caja del día", permiso: "pagos.read" }];
    case "morosidad":
      return [{ to: "/morosidad", etiqueta: "Ver morosidad", permiso: "cortes.read" }];
  }
}

/** Destino de la fase, o `null` si el usuario no puede abrir ninguno. */
export function destinoDeFase(
  fase: CicloFase,
  ctx: ContextoDestinoFase,
): DestinoFase | null {
  return candidatos(fase, ctx).find((d) => ctx.tienePermiso(d.permiso)) ?? null;
}
